import { NetworkReverseMap } from './Network';
import { ScriptType } from './ScriptType';

/**
 * Extended Public Key Prefix Module
 *
 * This module defines all standard Bitcoin extended public key (xpub) prefixes
 * and provides utilities for working with them. Each prefix indicates both the
 * network (mainnet/testnet) and the script type (P2PKH, P2WPKH, etc.).
 */

// ============================================================================
// 1. ENUM: Source of Truth for Extended Public Key Prefixes
// ============================================================================

/**
 * All valid extended public key prefixes for Bitcoin.
 *
 * Mainnet prefixes:
 * - xpub: Legacy addresses (BIP44, P2PKH)
 * - ypub: Nested SegWit addresses (BIP49, P2SH-P2WPKH)
 * - zpub: Native SegWit addresses (BIP84, P2WPKH)
 * - trpub: Taproot addresses (BIP86, P2TR)
 *
 * Testnet prefixes:
 * - tpub: Legacy addresses (BIP44, P2PKH)
 * - upub: Nested SegWit addresses (BIP49, P2SH-P2WPKH)
 * - vpub: Native SegWit addresses (BIP84, P2WPKH)
 * - trpubt: Taproot addresses (BIP86, P2TR)
 */
export enum ExtendedPubKeyPrefix {
  // Mainnet prefixes
  XPUB = 'xpub', // Legacy (BIP44, P2PKH)
  YPUB = 'ypub', // Nested SegWit (BIP49, P2SH-P2WPKH)
  ZPUB = 'zpub', // Native SegWit (BIP84, P2WPKH)
  TRPUB = 'trpub', // Taproot (BIP86, P2TR)

  // Testnet prefixes
  TPUB = 'tpub', // Legacy (BIP44, P2PKH)
  UPUB = 'upub', // Nested SegWit (BIP49, P2SH-P2WPKH)
  VPUB = 'vpub', // Native SegWit (BIP84, P2WPKH)
  TRPUBT = 'trpubt', // Taproot (BIP86, P2TR)

  // Unknown/unsupported prefix
  UNKNOWN = 'unknown',
}

/**
 * Union type of all extended public key prefix values.
 * Equivalent to: "xpub" | "ypub" | "zpub" | "trpub" | "tpub" | "upub" | "vpub" | "trpubt" | "unknown"
 */
export type ExtendedPubKeyPrefixValue = `${ExtendedPubKeyPrefix}`;

// ============================================================================
// 2. DISPLAY MAP: Prefix → User-Friendly Label
// ============================================================================

/**
 * Maps each prefix to its capitalized display label for UI purposes.
 * Example: "xpub" → "Xpub", "trpub" → "Trpub"
 */
export const ExtendedPubKeyDisplayMap = {
  [ExtendedPubKeyPrefix.XPUB]: 'Xpub',
  [ExtendedPubKeyPrefix.YPUB]: 'Ypub',
  [ExtendedPubKeyPrefix.ZPUB]: 'Zpub',
  [ExtendedPubKeyPrefix.TRPUB]: 'Trpub',

  [ExtendedPubKeyPrefix.TPUB]: 'Tpub',
  [ExtendedPubKeyPrefix.UPUB]: 'Upub',
  [ExtendedPubKeyPrefix.VPUB]: 'Vpub',
  [ExtendedPubKeyPrefix.TRPUBT]: 'Trpubt',

  [ExtendedPubKeyPrefix.UNKNOWN]: 'Unknown',
} as const;

/**
 * Union type of all display labels.
 * Equivalent to: "Xpub" | "Ypub" | "Zpub" | "Trpub" | "Tpub" | "Upub" | "Vpub" | "Trpubt" | "Unknown"
 */
export type ExtendedPubKeyDisplayLabel = (typeof ExtendedPubKeyDisplayMap)[ExtendedPubKeyPrefix];

// ============================================================================
// 3. NETWORK MAP: Prefix → Network Type
// ============================================================================

/**
 * Maps each prefix to its corresponding network (MAINNET, TESTNET, or UNKNOWN).
 * This allows quick determination of which network a given xpub belongs to.
 */
export const ExtendedPubKeyNetworkMap = {
  [ExtendedPubKeyPrefix.XPUB]: NetworkReverseMap.mainnet,
  [ExtendedPubKeyPrefix.YPUB]: NetworkReverseMap.mainnet,
  [ExtendedPubKeyPrefix.ZPUB]: NetworkReverseMap.mainnet,
  [ExtendedPubKeyPrefix.TRPUB]: NetworkReverseMap.mainnet,

  [ExtendedPubKeyPrefix.TPUB]: NetworkReverseMap.testnet,
  [ExtendedPubKeyPrefix.UPUB]: NetworkReverseMap.testnet,
  [ExtendedPubKeyPrefix.VPUB]: NetworkReverseMap.testnet,
  [ExtendedPubKeyPrefix.TRPUBT]: NetworkReverseMap.testnet,

  [ExtendedPubKeyPrefix.UNKNOWN]: 'UNKNOWN',
} as const;

/**
 * Union type of all network classifications.
 * Equivalent to: "MAINNET" | "TESTNET" | "UNKNOWN"
 */
export type ExtendedPubKeyNetworkClass = (typeof ExtendedPubKeyNetworkMap)[ExtendedPubKeyPrefix];

/**
 * Checks if the given prefix belongs to mainnet.
 * @param prefix - The extended public key prefix to check
 * @returns true if the prefix is a mainnet prefix, false otherwise
 */
export function isMainnetPrefix(prefix: ExtendedPubKeyPrefixValue): boolean {
  return ExtendedPubKeyNetworkMap[prefix] === NetworkReverseMap.mainnet;
}

/**
 * Checks if the given prefix belongs to testnet.
 * @param prefix - The extended public key prefix to check
 * @returns true if the prefix is a testnet prefix, false otherwise
 */
export function isTestnetPrefix(prefix: ExtendedPubKeyPrefixValue): boolean {
  return ExtendedPubKeyNetworkMap[prefix] === NetworkReverseMap.testnet;
}

/**
 * Extracts and identifies the extended public key prefix from an extended public key string.
 *
 * This function analyzes the beginning of an extended public key string (xpub, ypub, zpub, etc.)
 * and determines which type of key it is. It handles all standard Bitcoin mainnet and testnet
 * extended public key formats.
 *
 * **How it works:**
 * 1. Extracts the first 4-6 characters from the extended public key string
 * 2. Normalizes to lowercase for comparison
 * 3. Validates against known prefix values
 * 4. Returns the identified prefix or UNKNOWN if not recognized
 *
 * **Supported prefixes:**
 * - Mainnet: xpub, ypub, zpub, trpub
 * - Testnet: tpub, upub, vpub, trpubt
 *
 * @param extendedPubKey - The full extended public key string (e.g., "xpub6D4BDPc...")
 * @returns The identified prefix value or ExtendedPubKeyPrefix.UNKNOWN if not recognized
 *
 * @example
 * ```typescript
 * // Detect mainnet legacy prefix
 * const prefix1 = getPrefixFromExtendedPublicKey('xpub6CzbQ5BLF2mngsMXijEf...');
 * console.log(prefix1); // 'xpub'
 *
 * // Detect mainnet native SegWit prefix
 * const prefix2 = getPrefixFromExtendedPublicKey('zpub6rFR7y4Q2AijBEqTUquh...');
 * console.log(prefix2); // 'zpub'
 *
 * // Detect testnet prefix
 * const prefix3 = getPrefixFromExtendedPublicKey('tpub6D4BDPcP2GT577Vvch3R...');
 * console.log(prefix3); // 'tpub'
 *
 * // Handle unknown prefix
 * const prefix4 = getPrefixFromExtendedPublicKey('invalidkey123...');
 * console.log(prefix4); // 'unknown'
 *
 * // Use with other functions
 * const key = 'zpub6rFR7y4Q2AijBEqTUquh...';
 * const prefix = getPrefixFromExtendedPublicKey(key);
 *
 * if (prefix !== ExtendedPubKeyPrefix.UNKNOWN) {
 *   const network = ExtendedPubKeyNetworkMap[prefix];
 *   const scriptType = ExtendedPubKeyScriptTypeMap[prefix];
 *   const versions = getBip32VersionBytes(prefix);
 *   console.log({ prefix, network, scriptType, versions });
 * }
 * ```
 *
 * @see {@link ExtendedPubKeyPrefix} for all valid prefix values
 * @see {@link isMainnetPrefix} to check if the detected prefix is mainnet
 * @see {@link isTestnetPrefix} to check if the detected prefix is testnet
 */
export function getPrefixFromExtendedPublicKey(extendedPubKey: string): ExtendedPubKeyPrefixValue {
  // Extract potential prefix (first 4-6 characters, normalized to lowercase)
  // We check up to 6 characters to support 'trpub' and 'trpubt' (6 chars)
  const potentialPrefix = extendedPubKey.slice(0, 6).toLowerCase();

  // Get all valid prefix values
  const validPrefixes: string[] = Object.values(ExtendedPubKeyPrefix);

  // Check for 6-character prefixes first (trpub, trpubt)
  if (validPrefixes.includes(potentialPrefix)) {
    return potentialPrefix as ExtendedPubKeyPrefixValue;
  }

  // Check for 4-character prefixes (xpub, ypub, zpub, tpub, upub, vpub)
  const shortPrefix = potentialPrefix.slice(0, 4);
  if (validPrefixes.includes(shortPrefix)) {
    return shortPrefix as ExtendedPubKeyPrefixValue;
  }

  // Unknown prefix
  return ExtendedPubKeyPrefix.UNKNOWN;
}

// ============================================================================
// 4. SCRIPT TYPE MAP: Prefix → Script Type
// ============================================================================

/**
 * Maps each prefix to its corresponding Bitcoin script type.
 * This determines the address format and locking script used.
 *
 * Note: UNKNOWN prefix maps to undefined as we cannot determine the script type.
 */
export const ExtendedPubKeyScriptTypeMap = {
  [ExtendedPubKeyPrefix.XPUB]: ScriptType.P2PKH,
  [ExtendedPubKeyPrefix.YPUB]: ScriptType.P2SH_P2WPKH,
  [ExtendedPubKeyPrefix.ZPUB]: ScriptType.P2WPKH,
  [ExtendedPubKeyPrefix.TRPUB]: ScriptType.P2TR,

  [ExtendedPubKeyPrefix.TPUB]: ScriptType.P2PKH,
  [ExtendedPubKeyPrefix.UPUB]: ScriptType.P2SH_P2WPKH,
  [ExtendedPubKeyPrefix.VPUB]: ScriptType.P2WPKH,
  [ExtendedPubKeyPrefix.TRPUBT]: ScriptType.P2TR,

  [ExtendedPubKeyPrefix.UNKNOWN]: undefined,
} as const;

/**
 * Union type of all possible script types (including undefined for UNKNOWN).
 */
export type ExtendedPubKeyScriptType = (typeof ExtendedPubKeyScriptTypeMap)[ExtendedPubKeyPrefix];

/**
 * Attempts to determine the script type from an extended public key prefix.
 * @param prefix - The extended public key prefix
 * @returns The corresponding ScriptType, or undefined if unknown
 */
export function guessScriptTypeFromPrefix(
  prefix: ExtendedPubKeyPrefixValue
): ScriptType | undefined {
  return ExtendedPubKeyScriptTypeMap[prefix];
}

// ============================================================================
// 5. BIP32 VERSION BYTES MAP: Prefix → Version Bytes
// ============================================================================

/**
 * Structure for BIP32 version bytes.
 * Version bytes are 4-byte integers used in the serialization of extended keys
 * according to BIP32 (Hierarchical Deterministic Wallets).
 *
 * @see https://github.com/bitcoin/bips/blob/master/bip-0032.mediawiki
 */
export interface Bip32VersionBytes {
  /** Public key version bytes (4 bytes as hex integer) */
  public: number;
  /** Private key version bytes (4 bytes as hex integer) */
  private: number;
}

/**
 * Maps each extended public key prefix to its corresponding BIP32 version bytes.
 *
 * These version bytes are used when serializing/deserializing extended public and
 * private keys. Each prefix has different version bytes that identify the key type
 * and network when encoded in Base58Check format.
 *
 * **How BIP32 Version Bytes Work:**
 *
 * Extended keys (xpub/xprv) are serialized using a specific format that includes:
 * - 4 bytes: version bytes (identifies the key purpose and network)
 * - 1 byte: depth in the derivation tree
 * - 4 bytes: parent fingerprint
 * - 4 bytes: child number
 * - 32 bytes: chain code
 * - 33 bytes: public or private key data
 *
 * The version bytes are the first 4 bytes and determine how the key should be
 * interpreted and what type of addresses it can generate.
 *
 * **Mainnet Version Bytes:**
 * - xpub/xprv: 0x0488b21e / 0x0488ade4 - BIP44 Legacy (P2PKH)
 * - ypub/yprv: 0x049d7cb2 / 0x049d7878 - BIP49 Nested SegWit (P2SH-P2WPKH)
 * - zpub/zprv: 0x04b24746 / 0x04b2430c - BIP84 Native SegWit (P2WPKH)
 * - trpub: 0x0488b21e / 0x0488ade4 - BIP86 Taproot (P2TR) - reuses xpub bytes
 *
 * **Testnet Version Bytes:**
 * - tpub/tprv: 0x043587cf / 0x04358394 - BIP44 Legacy (P2PKH)
 * - upub/uprv: 0x044a5262 / 0x044a4e28 - BIP49 Nested SegWit (P2SH-P2WPKH)
 * - vpub/vprv: 0x045f1cf6 / 0x045f18bc - BIP84 Native SegWit (P2WPKH)
 * - trpubt: 0x043587cf / 0x04358394 - BIP86 Taproot (P2TR) - reuses tpub bytes
 *
 * **Important Notes:**
 *
 * 1. **Taproot Prefixes:** The Taproot prefixes (trpub/trpubt) are non-standard and
 *    reuse the same version bytes as xpub/tpub. This is because BIP86 (Taproot)
 *    wasn't originally part of the extended key prefix specification. Some wallets
 *    use the same xpub format for Taproot keys but derive them using BIP86 paths.
 *
 * 2. **Network Detection:** Version bytes can be used to detect if an extended key
 *    belongs to mainnet or testnet by examining the bytes.
 *
 * 3. **Base58Check Encoding:** These version bytes are included in the Base58Check
 *    encoding of extended keys, which is why different prefixes (xpub vs zpub)
 *    result in different string representations.
 *
 * @example
 * ```typescript
 * // Get version bytes for zpub (Native SegWit mainnet)
 * const zpubVersions = ExtendedPubKeyBip32VersionMap.zpub;
 * console.log(zpubVersions.public.toString(16));  // '4b24746'
 * console.log(zpubVersions.private.toString(16)); // '4b2430c'
 *
 * // Get version bytes for testnet tpub
 * const tpubVersions = ExtendedPubKeyBip32VersionMap.tpub;
 * console.log(tpubVersions.public.toString(16));  // '43587cf'
 * ```
 *
 * @see https://github.com/bitcoin/bips/blob/master/bip-0032.mediawiki - BIP32 Specification
 * @see https://github.com/bitcoin/bips/blob/master/bip-0049.mediawiki - BIP49 (ypub)
 * @see https://github.com/bitcoin/bips/blob/master/bip-0084.mediawiki - BIP84 (zpub)
 * @see https://github.com/bitcoin/bips/blob/master/bip-0086.mediawiki - BIP86 (Taproot)
 */
export const ExtendedPubKeyBip32VersionMap: Record<
  Exclude<ExtendedPubKeyPrefixValue, 'unknown'>,
  Bip32VersionBytes
> = {
  // Mainnet prefixes
  xpub: { public: 0x0488b21e, private: 0x0488ade4 },
  ypub: { public: 0x049d7cb2, private: 0x049d7878 },
  zpub: { public: 0x04b24746, private: 0x04b2430c },
  trpub: { public: 0x0488b21e, private: 0x0488ade4 }, // Taproot uses same as xpub

  // Testnet prefixes
  tpub: { public: 0x043587cf, private: 0x04358394 },
  upub: { public: 0x044a5262, private: 0x044a4e28 },
  vpub: { public: 0x045f1cf6, private: 0x045f18bc },
  trpubt: { public: 0x043587cf, private: 0x04358394 }, // Taproot testnet uses same as tpub
} as const;

/**
 * Gets the BIP32 version bytes for a given extended public key prefix.
 *
 * This is a convenience function that provides type-safe access to version bytes
 * with proper error handling for unknown prefixes.
 *
 * @param prefix - The extended public key prefix
 * @returns The version bytes for public and private keys, or undefined for unknown prefixes
 *
 * @example
 * ```typescript
 * // Get version bytes for zpub
 * const versions = getBip32VersionBytes('zpub');
 * if (versions) {
 *   console.log('Public version:', versions.public.toString(16));
 *   console.log('Private version:', versions.private.toString(16));
 * }
 *
 * // Handle unknown prefix
 * const unknown = getBip32VersionBytes('unknown');
 * console.log(unknown); // undefined
 * ```
 */
export function getBip32VersionBytes(
  prefix: ExtendedPubKeyPrefixValue
): Bip32VersionBytes | undefined {
  if (prefix === ExtendedPubKeyPrefix.UNKNOWN) {
    return undefined;
  }
  return ExtendedPubKeyBip32VersionMap[prefix];
}
